import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthService,
  UiService,
  UserProfile,
  UserProfileService,
  WorkspaceService,
  WorkspaceWithServices,
} from '@keeps-platform-frontend-workspace/kp-keeps';
import { KpMenuApp } from '@keeps-platform-frontend-workspace/ui/kp-menu';
import { environment } from 'environments/environment';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { JobDataService, JobFunctionService } from './shared/store/data-services';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnDestroy, OnInit {
  private _unsubscribeAll: Subject<Subscription>;

  user$: Observable<UserProfile>;
  apps$: Observable<KpMenuApp[]>;
  isAdmin$: Observable<boolean>;
  currentWorkspace$: Observable<WorkspaceWithServices | undefined>;
  baseHref$: Observable<string>;
  baseHref: string;

  /**
   * Constructor
   *
   * @param document
   * @param _authService
   * @param _userProfileService
   * @param _uiService
   * @param _router
   * @param _workspaceService
   * @param _jobService
   * @param _jobFunctionService
   */
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private _authService: AuthService,
    private _userProfileService: UserProfileService,
    private _uiService: UiService,
    private _router: Router,
    private _workspaceService: WorkspaceService,
    private _jobService: JobDataService,
    private _jobFunctionService: JobFunctionService,
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    // Get user
    this.user$ = this._userProfileService.profile$;

    // Get apps
    this.apps$ = this._uiService.userApplications$;

    this.isAdmin$ = this._userProfileService.isAdmin$();
    this.currentWorkspace$ = this._workspaceService.currentWorkspace$;
    this.baseHref$ = this.currentWorkspace$.pipe(
      filter((workspace) => !!workspace),
      map((workspace) => {
        this.baseHref = workspace.hash_id;
        return this.baseHref;
      }),
    );
  }

  ngOnInit(): void {
    this._userProfileService.profile$
      .pipe(
        filter((user) => !!user?.id),
        takeUntil(this._unsubscribeAll),
      )
      .subscribe(() => this.initDataServices());
  }

  private initDataServices() {
    this._jobService.getAll();
    this._jobFunctionService.getAll();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  logout() {
    this._authService.logout();
  }

  profile(): void {
    const isMyAccount = environment.name === 'MyAccount';
    if (isMyAccount) {
      this._router.navigate(['/user', 'profile']);
      return;
    }

    window.open(`${environment.apps.myAccount.url}profile`, '_blank');
  }
}
